import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import identityApi from '../clients/IdentityClient';
import driverApi from '../clients/DriverClient';
import { useAuth } from '../context/AuthContext';
import { UserEmailUpdateRequest } from '../models/requestBodies/UpdateUserRequestBody';
import TextInputField from './shared/TextInputField';
import './AccountSettings.css';
import ButtonSelect from './shared/ButtonSelect';
import { formatLocation } from '../utils/bookingUtils';
import { currentTenant } from '../tenant/tenants';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailChangeCooldownMessage = 'We have sent a confirmation email to your new address. Next 2 minutes you will not be able to request another email change.';

const AccountSettings: React.FC = () => {
  const { user, refreshUserProfile } = useAuth();
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [emailError, setEmailError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [locations, setLocations] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState(user?.preferredLocation || '');
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  useEffect(() => {
    // The venue list is World of Racing's; a brand without booking has no venue to prefer.
    if (!currentTenant.features.booking) return;
    const loadLocations = async () => {
        const result = await driverApi.getBookingLocations();
        setLocations(result);
    };

    loadLocations();
  }, []);

  if (!user) {
    return null;
  }

  const resetMessages = () => {
    setEmailError('');
    setGeneralError('');
    setSuccessMessage('');
  };

  const startEditingEmail = () => {
    setNewEmail('');
    resetMessages();
    setIsEditingEmail(true);
  };

  const cancelEditingEmail = () => {
    setNewEmail(user.email);
    resetMessages();
    setIsEditingEmail(false);
  };

  const validateEmailForm = (): string => {
    const normalizedNewEmail = newEmail.trim().toLowerCase();
    const currentEmail = user.email.trim().toLowerCase();

    if (!normalizedNewEmail) {
      return 'Enter your new email address';
    }

    if (!emailRegex.test(normalizedNewEmail)) {
      return 'Enter a valid email address';
    }

    if (normalizedNewEmail === currentEmail) {
      return 'Enter an email address different from your current one';
    }

    return '';
  };

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isEditingEmail) {
      startEditingEmail();
      return;
    }

    resetMessages();

    const validationMessage = validateEmailForm();
    if (validationMessage) {
      setEmailError(validationMessage);
      return;
    }

    setIsSaving(true);

    try {
      const normalizedEmail = newEmail.trim().toLowerCase();
      const payload: UserEmailUpdateRequest = {
        newEmail: normalizedEmail
      };
    
      await identityApi.updateUserEmail(payload);

      setNewEmail(normalizedEmail);
      setSuccessMessage(emailChangeCooldownMessage);
      setIsEditingEmail(false);
    } catch (error) {
      setGeneralError('Failed to update email. Please make sure it has been at least 10 minutes since your last email change and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  async function handleLocationChange(location: string) {
    setIsSavingLocation(true);
    await identityApi.patchUser(location);
    setSelectedLocation(location);
    await refreshUserProfile();
    setIsSavingLocation(false);
  }

  return (
    <div>
      <div className="container account-settings-page">
        <div className="account-settings-header ">
          <div className="flex-grow-1 text-center">
            <h1 className="heading mb-3 ">Manage your account</h1>
          </div>
          <Link to="/dashboard" className="btn btn-outline-light settings-back-link">
            Back to Dashboard
          </Link>
        </div>

        <div className="row g-4 justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">
            <div className="card settings-section-card">

              {successMessage && (
                <div className="alert alert-success mt-4" role="alert">
                  {successMessage}
                </div>
              )}

              {generalError && (
                <div className="alert alert-danger mt-4" role="alert">
                  {generalError}
                </div>
              )}

              <form className="settings-card-element" onSubmit={handleEmailSubmit}>
                <label htmlFor="newEmail" className="settings-field-label">Email address:</label>
                <div className="settings-email-row">
                  <div className="settings-email-input-wrap">
                    <TextInputField
                      id="newEmail"
                      name="newEmail"
                      type="email"
                      placeholder="Enter your new email"
                      value={newEmail}
                      onChange={(event) => setNewEmail(event.target.value)}
                      error={emailError}
                      disabled={isSaving || !isEditingEmail}
                      required
                    />
                  </div>
                  {currentTenant.features.emailChange && (
                  <button
                    type={isEditingEmail ? 'submit' : 'button'}
                    className="btn btn-primary settings-email-action"
                    onClick={!isEditingEmail ? startEditingEmail : undefined}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : isEditingEmail ? 'Save' : 'Edit'}
                  </button>
                  )}
                </div>
                {isEditingEmail && (
                  <div className="settings-form-actions">
                    <button type="button" className="btn btn-outline-light" onClick={cancelEditingEmail} disabled={isSaving}>
                      Cancel
                    </button>
                  </div>
                )}
              </form>
              {currentTenant.features.booking && (
              <div className="settings-card-element">
                {locations.length === 0 ? (
                  <p className="settings-form-note">Loading locations...</p>
                ) : (
                  <ButtonSelect
                    options={locations}
                    title="Your favorite location:"
                    selected={selectedLocation}
                    onChange={handleLocationChange}
                    disabled={isSavingLocation}
                    renderLabel={formatLocation} />
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;